import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { FaPlus } from 'react-icons/fa';
import { useToast } from '../contexts/ToastContext';
import TaskCard from '../components/tasks/TaskCard';
import AddTaskModal from '../components/tasks/AddTaskModal';
import EditTaskModal from '../components/tasks/EditTaskModal';
import DeleteTaskModal from '../components/tasks/DeleteTaskModal';

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const addToast = useToast();

  const fetchTasks = useCallback(async () => {
    const { data, error } = await supabase
      .from('tasks')
      .select(`
        id, 
        name, 
        description, 
        priority, 
        created_at,
        projects!inner (id, name, project_groups!inner (id, name))
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tasks:', error);
      addToast('Error fetching tasks: ' + error.message, 'danger');
    } else {
      setTasks(data || []);
    }
  }, [addToast]);

  const fetchProjects = useCallback(async () => {
    const { data, error } = await supabase
      .from('projects')
      .select('id, name, project_groups!inner (id, name)')
      .order('name');

    if (error) {
      console.error('Error fetching projects:', error);
      addToast('Error fetching projects: ' + error.message, 'danger');
    } else {
      setProjects(data || []);
    }
  }, [addToast]);

  useEffect(() => {
    fetchTasks();
    fetchProjects();
  }, [fetchTasks, fetchProjects]);

  const filteredTasks = selectedProjectId 
    ? tasks.filter(task => task.projects?.id === selectedProjectId)
    : tasks;

  const handleTaskAdded = (newTask) => {
    setTasks([...tasks, newTask]);
  };

  const handleTaskUpdated = (updatedTask) => {
    setTasks(tasks.map(t => t.id === updatedTask.id ? updatedTask : t));
  };

  const handleTaskDeleted = (deletedTaskId) => {
    setTasks(tasks.filter(t => t.id !== deletedTaskId));
  };

  const openAddTaskModal = () => {
    setShowAddTaskModal(true);
  };

  const openEditTaskModal = (task) => {
    setCurrentTask(task);
    setShowEditTaskModal(true);
  };

  const openDeleteTaskModal = (task) => {
    setCurrentTask(task);
    setShowDeleteTaskModal(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-0">Tasks</h1>
        <button
          onClick={openAddTaskModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
        >
          <FaPlus className="mr-2" />
          Add Task
        </button>
      </div>

      <div className="mb-6">
        <div className="max-w-sm">
          <label htmlFor="project-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Filter by Project
          </label>
          <select
            id="project-filter"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">All Projects</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name} ({project.project_groups?.name})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredTasks.map(task => (
          <TaskCard 
            key={task.id}
            task={task}
            viewMode="card"
            onEdit={openEditTaskModal}
            onDelete={openDeleteTaskModal}
          />
        ))}
      </div>

      {filteredTasks.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">
            {selectedProjectId ? 'No tasks found for the selected project.' : 'No tasks found. Create your first task!'}
          </p>
        </div>
      )}

      <AddTaskModal
        show={showAddTaskModal}
        handleClose={() => setShowAddTaskModal(false)}
        projects={projects}
        onTaskAdded={handleTaskAdded}
      />

      <EditTaskModal
        show={showEditTaskModal}
        handleClose={() => setShowEditTaskModal(false)}
        task={currentTask}
        projects={projects}
        onTaskUpdated={handleTaskUpdated}
      />

      <DeleteTaskModal
        show={showDeleteTaskModal}
        handleClose={() => setShowDeleteTaskModal(false)}
        task={currentTask}
        onTaskDeleted={handleTaskDeleted}
      />
    </div>
  );
}

export default Tasks;